import React, { useState } from 'react';

interface CVUploadProps {
  onUpload: (file: File) => Promise<void>;
  loading: boolean;
}

const CVUpload: React.FC<CVUploadProps> = ({ onUpload, loading }) => {
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear previous errors
    setError(null);

    // Strict file type validation
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setError('Please upload a PDF or Word document (PDF, DOC, DOCX)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size should be less than 5MB');
      return;
    }

    try {
      await onUpload(file);
    } catch (err: any) {
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        setError(Array.isArray(detail) ? detail[0].msg : detail);
      } else {
        setError(err.message || 'Failed to upload file');
      }
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded-lg shadow">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleFileChange}
          className="hidden"
          id="cv-upload"
          disabled={loading}
        />
        <label
          htmlFor="cv-upload"
          className={`cursor-pointer text-blue-600 hover:text-blue-700 ${loading ? 'opacity-50' : ''}`}
        >
          {loading ? 'Analyzing...' : 'Click to upload your CV'}
        </label>
        <p className="text-sm text-gray-500 mt-2">
          Supported formats: PDF, DOC, DOCX (max 5MB)
        </p>
        {error && (
          <p className="text-red-600 text-sm mt-2">{error}</p>
        )}
      </div>
    </div>
  );
};

export default CVUpload; 